import { EChartsOption } from 'echarts'
import ReactECharts from 'echarts-for-react'

import { Loading, TrendLabel } from '@/components/customComponents'
import { Card } from '@/components/ui/card'

import { chartColors } from '@/constants/COLOR_PALETTE'
import { CountryFilterType } from '@/store/filterSlice'
import { PnLMetricCardModel } from '@/types/clarity'
import { formatNumber } from '@/utils'

interface MiniMetricConfig {
  key: keyof PnLMetricCardModel
  header: string
  icon: React.ReactNode
  isIncreasingPositive?: boolean
  className?: string
  /** Tooltip: 'money' shows amount, 'percentage' shows value. Default 'percentage'. */
  tooltipDisplayType?: 'money' | 'percentage'
}

interface MasterStackBarChartProps {
  title?: string
  metricCardRecord: Record<string, PnLMetricCardModel>
  miniCardData?: PnLMetricCardModel
  trendData?: PnLMetricCardModel
  loading?: boolean
  miniMetricConfigs: MiniMetricConfig[]
  chartDataConfigs: MiniMetricConfig[]
  country?: CountryFilterType
  customComponent?: React.ReactNode
}

export function MasterStackBarChart({
  title = 'Breakdown',
  metricCardRecord,
  miniCardData,
  trendData,
  loading = false,
  miniMetricConfigs,
  chartDataConfigs,
  country,
  customComponent,
}: MasterStackBarChartProps) {
  return (
    <Card>
      {/* Card Header */}
      <div className="flex justify-between items-center p-8 pb-0">
        <h3 className="text-2xl">{title}</h3>
        {customComponent && !loading && customComponent}
      </div>
      {loading ? (
        <div className="mb-8">
          <Loading />
        </div>
      ) : (
        <>
          {/* Mini Metrics */}
          {miniCardData && (
            <MiniMetrics
              miniCardData={miniCardData}
              trendData={trendData}
              miniMetricConfigs={miniMetricConfigs}
              country={country}
            />
          )}
          {/* Chart */}
          <div className="pb-4">
            <ReactECharts
              option={stackedBarChartOptions(
                metricCardRecord,
                chartDataConfigs,
                country
              )}
            />
          </div>
        </>
      )}
    </Card>
  )
}

function MiniMetrics({
  miniCardData,
  trendData,
  miniMetricConfigs,
  country,
}: {
  miniCardData: PnLMetricCardModel
  trendData?: PnLMetricCardModel
  miniMetricConfigs: MiniMetricConfig[]
  country?: CountryFilterType
}) {
  return (
    <div
      className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 p-8 pb-0`}
    >
      {miniMetricConfigs?.map((config) => (
        <MiniMetricCard
          key={config.key}
          header={config.header}
          metricValue={miniCardData[config.key].value}
          trendValue={trendData?.[config.key].value}
          metricType={miniCardData[config.key].metricType}
          icon={config.icon}
          country={country}
          className={config.className}
          isIncreasingPositive={config.isIncreasingPositive}
        />
      ))}
    </div>
  )
}

function MiniMetricCard({
  header,
  metricValue,
  trendValue,
  metricType,
  icon,
  country,
  className,
  isIncreasingPositive = true,
}: {
  header: string
  metricValue: number
  trendValue?: number
  metricType: 'money' | 'percentage' | 'number' | 'hour'
  icon: React.ReactNode
  country?: CountryFilterType
  className?: string
  isIncreasingPositive?: boolean
}) {
  return (
    <div className={`flex ${className || ''}`}>
      <div className="flex">{icon}</div>
      <div className="flex flex-col p-0.5">
        <span className="text-sm text-black-200">{header}</span>
        <div className="flex items-center gap-1">
          <span className="text-xl">
            {formatNumber(metricValue, metricType, true, country)}
          </span>
          <TrendLabel
            newValue={metricValue}
            oldValue={trendValue}
            trendType={metricType}
            verySmall
            country={country}
            isNormal={isIncreasingPositive}
          />
        </div>
      </div>
    </div>
  )
}

const stackedBarChartOptions = (
  data: Record<string, any>,
  chartDataConfigs: MiniMetricConfig[],
  country?: CountryFilterType
): EChartsOption => {
  const xAxisNames = Object.keys(data)

  const series: echarts.BarSeriesOption[] = chartDataConfigs.map(
    (config, index) => {
      const seriesData = xAxisNames.map((dateKey) => {
        const metricCard = data[dateKey]
        return metricCard?.[config.key]?.amount || 0
      })

      return {
        name: config.header,
        type: 'bar',
        stack: 'total',
        barWidth: '60%',
        data: seriesData,
        itemStyle: {
          borderRadius: 8,
          borderWidth: 1,
          borderColor: '#fff',
          color: chartColors[index % chartColors.length],
        },
      }
    }
  )

  return {
    legend: {
      selectedMode: false,
      orient: 'horizontal',
      bottom: '0%',
      left: 'center',
      data: chartDataConfigs.map((config) => config.header),
      icon: 'circle',
      itemGap: 20,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params: any) {
        const dateKey = params[0]?.axisValue
        const metricCard = dateKey ? data[dateKey] : undefined
        let result = (dateKey ?? '') + '<br/>'
        params.forEach((param: any) => {
          const config = chartDataConfigs[param.seriesIndex]
          const tooltipType = config?.tooltipDisplayType ?? 'percentage'
          const metric = metricCard?.[config?.key]
          const displayValue =
            tooltipType === 'money'
              ? (metric?.amount ?? param.value)
              : (metric?.value ?? param.value)
          const formatType = tooltipType === 'money' ? 'money' : 'percentage'
          result +=
            param.marker +
            ' ' +
            param.seriesName +
            ': ' +
            formatNumber(displayValue, formatType, true, country) +
            '<br/>'
        })
        return result
      },
    },
    grid: {
      bottom: '25%',
      left: '12%',
    },
    yAxis: {
      type: 'value',
    },
    center: ['50%', '60%'],
    xAxis: {
      type: 'category',
      data: xAxisNames,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    series,
  }
}
